import { Card, Tabs, Select, DatePicker, Table, Space, Button, message, Statistic, Row, Col, Tag } from 'antd'
import { CarOutlined, FileDoneOutlined, FileTextOutlined, ToolOutlined, FilterOutlined } from '@ant-design/icons'
import { useState, useEffect } from 'react'
import axios from 'axios'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker

const GROUP_BY_OPTIONS = [
  { label: 'يومي', value: 'daily' },
  { label: 'أسبوعي', value: 'weekly' },
  { label: 'شهري', value: 'monthly' },
  { label: 'ربع سنوي', value: 'quarterly' },
  { label: 'نصف سنوي', value: 'semiannual' },
  { label: 'سنوي', value: 'yearly' },
]

function TransportDashboard() {
  const token = localStorage.getItem('token')
  const headers = { Authorization: `Bearer ${token}` }

  // تبويب 1: أوامر النقل (عام/غير مقفل/مقفل)
  const [ordersDateRange, setOrdersDateRange] = useState([dayjs().startOf('month'), dayjs()])
  const [ordersGroupBy, setOrdersGroupBy] = useState('daily')
  const [ordersStatus, setOrdersStatus] = useState('all')
  const [ordersDateBasis, setOrdersDateBasis] = useState('order')
  const [ordersBreakdown, setOrdersBreakdown] = useState(null)
  const [ordersRows, setOrdersRows] = useState([])
  const [ordersLoading, setOrdersLoading] = useState(false)

  // تبويب 2: الفوترة
  const [invDateRange, setInvDateRange] = useState([dayjs().startOf('month'), dayjs()])
  const [invGroupBy, setInvGroupBy] = useState('daily')
  const [invStatus, setInvStatus] = useState('invoiced')
  const [invRows, setInvRows] = useState([])
  const [invLoading, setInvLoading] = useState(false)

  // تبويب 3: العقود
  const [contractRows, setContractRows] = useState([])
  const [contractLoading, setContractLoading] = useState(false)

  // تبويب 4: السيارات
  const [availableCars, setAvailableCars] = useState([])
  const [workshopCars, setWorkshopCars] = useState([])
  const [carsLoading, setCarsLoading] = useState(false)

  const fetchOrders = async () => {
    setOrdersLoading(true)
    try {
      const params = {
        date_from: dayjs(ordersDateRange[0]).format('YYYY-MM-DD'),
        date_to: dayjs(ordersDateRange[1]).format('YYYY-MM-DD'),
        group_by: ordersGroupBy,
        status: ordersStatus,
        date_basis: ordersStatus === 'open' ? 'order' : ordersDateBasis
      }
      if (ordersBreakdown) params.breakdown = ordersBreakdown
      const res = await axios.get('/api/settings/transport/orders-count', { headers, params })
      setOrdersRows(res.data)
    } catch (err) {
      message.error(err.response?.data?.error || 'خطأ في جلب أوامر النقل')
    }
    setOrdersLoading(false)
  }

  const fetchInvoicing = async () => {
    setInvLoading(true)
    try {
      const params = {
        date_from: dayjs(invDateRange[0]).format('YYYY-MM-DD'),
        date_to: dayjs(invDateRange[1]).format('YYYY-MM-DD'),
        group_by: invGroupBy,
        status: invStatus
      }
      const res = await axios.get('/api/settings/transport/invoicing-count', { headers, params })
      setInvRows(res.data)
    } catch (err) {
      message.error(err.response?.data?.error || 'خطأ في جلب بيانات الفوترة')
    }
    setInvLoading(false)
  }

  const fetchContracts = async () => {
    setContractLoading(true)
    try {
      const res = await axios.get('/api/settings/transport/contracts', { headers })
      setContractRows(res.data)
    } catch (err) {
      message.error(err.response?.data?.error || 'خطأ في جلب العقود')
    }
    setContractLoading(false)
  }

  const fetchCars = async () => {
    setCarsLoading(true)
    try {
      const [availRes, workshopRes] = await Promise.all([
        axios.get('/api/settings/transport/available-cars', { headers }),
        axios.get('/api/settings/transport/workshop-cars', { headers })
      ])
      setAvailableCars(availRes.data)
      setWorkshopCars(workshopRes.data)
    } catch (err) {
      message.error(err.response?.data?.error || 'خطأ في جلب بيانات السيارات')
    }
    setCarsLoading(false)
  }

  useEffect(() => {
    fetchOrders()
    fetchInvoicing()
    fetchContracts()
    fetchCars()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const totalOrders = ordersRows.reduce((s, r) => s + r.count, 0)
  const totalInvoicing = invRows.reduce((s, r) => s + r.count, 0)
  const totalContracts = contractRows.reduce((s, r) => s + r.count, 0)

  const ordersColumns = [
    { title: 'الفترة', dataIndex: 'period', key: 'period' },
    ...(ordersBreakdown ? [{
      title: ordersBreakdown === 'driver' ? 'السائق' : 'العميل',
      dataIndex: 'breakdownLabel', key: 'breakdownLabel'
    }] : []),
    { title: 'عدد الأوامر', dataIndex: 'count', key: 'count', align: 'left',
      render: v => <strong>{v.toLocaleString()}</strong> },
  ]

  const invColumns = [
    { title: 'الفترة', dataIndex: 'period', key: 'period' },
    { title: 'العدد', dataIndex: 'count', key: 'count', align: 'left',
      render: v => <strong>{v.toLocaleString()}</strong> },
  ]

  const contractColumns = [
    { title: 'العميل', dataIndex: 'customerName', key: 'customerName' },
    { title: 'عدد العقود الفعالة', dataIndex: 'count', key: 'count', align: 'left',
      render: v => <strong style={{ color: '#0f8a45' }}>{v}</strong> },
  ]

  const carColumns = [
    { title: 'كود السيارة', dataIndex: 'Code', key: 'Code', width: 110 },
    { title: 'اسم السيارة', dataIndex: 'Name', key: 'Name' },
  ]

  const items = [
    {
      key: 'orders',
      label: <span><FileTextOutlined /> أوامر النقل</span>,
      children: (
        <Card>
          <Space size="middle" wrap align="end" style={{ marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 12, color: '#1c599e', fontWeight: 600, marginBottom: 4 }}>الفترة</div>
              <RangePicker value={ordersDateRange} onChange={setOrdersDateRange} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#1c599e', fontWeight: 600, marginBottom: 4 }}>الحالة</div>
              <Select
                value={ordersStatus}
                style={{ width: 150 }}
                onChange={setOrdersStatus}
                options={[
                  { label: 'العامة (الكل)', value: 'all' },
                  { label: 'غير مقفلة', value: 'open' },
                  { label: 'مقفلة', value: 'closed' },
                ]}
              />
            </div>
            {ordersStatus !== 'open' && (
              <div>
                <div style={{ fontSize: 12, color: '#1c599e', fontWeight: 600, marginBottom: 4 }}>التاريخ المعتمَد</div>
                <Select
                  value={ordersDateBasis}
                  style={{ width: 150 }}
                  onChange={setOrdersDateBasis}
                  options={[
                    { label: 'تاريخ الأمر', value: 'order' },
                    { label: 'تاريخ البيان', value: 'declare' },
                  ]}
                />
              </div>
            )}
            <div>
              <div style={{ fontSize: 12, color: '#1c599e', fontWeight: 600, marginBottom: 4 }}>التجميع</div>
              <Select value={ordersGroupBy} style={{ width: 130 }} onChange={setOrdersGroupBy} options={GROUP_BY_OPTIONS} />
            </div>
            {ordersStatus === 'all' && (
              <div>
                <div style={{ fontSize: 12, color: '#1c599e', fontWeight: 600, marginBottom: 4 }}>تفصيل حسب</div>
                <Select
                  value={ordersBreakdown}
                  style={{ width: 150 }}
                  allowClear
                  placeholder="بدون تفصيل"
                  onChange={setOrdersBreakdown}
                  options={[
                    { label: 'السائق', value: 'driver' },
                    { label: 'العميل', value: 'customer' },
                  ]}
                />
              </div>
            )}
            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>&nbsp;</div>
              <Button type="primary" icon={<FilterOutlined />} loading={ordersLoading} onClick={fetchOrders}>
                عرض
              </Button>
            </div>
          </Space>
          <Statistic title="إجمالي عدد الأوامر بالفترة المختارة" value={totalOrders} style={{ marginBottom: 16 }} />
          <Table columns={ordersColumns} dataSource={ordersRows} rowKey={(r, i) => i} loading={ordersLoading} pagination={{ pageSize: 20 }} size="small" />
        </Card>
      )
    },
    {
      key: 'invoicing',
      label: <span><FileDoneOutlined /> الفوترة</span>,
      children: (
        <Card>
          <Space size="middle" wrap align="end" style={{ marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 12, color: '#1c599e', fontWeight: 600, marginBottom: 4 }}>الفترة (حسب تاريخ البيان)</div>
              <RangePicker value={invDateRange} onChange={setInvDateRange} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#1c599e', fontWeight: 600, marginBottom: 4 }}>الحالة</div>
              <Select
                value={invStatus}
                style={{ width: 170 }}
                onChange={setInvStatus}
                options={[
                  { label: 'مفوترة', value: 'invoiced' },
                  { label: 'بحاجة إلى فوترة', value: 'needs_invoicing' },
                ]}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#1c599e', fontWeight: 600, marginBottom: 4 }}>التجميع</div>
              <Select value={invGroupBy} style={{ width: 130 }} onChange={setInvGroupBy} options={GROUP_BY_OPTIONS} />
            </div>
            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>&nbsp;</div>
              <Button type="primary" icon={<FilterOutlined />} loading={invLoading} onClick={fetchInvoicing}>
                عرض
              </Button>
            </div>
          </Space>
          <Statistic title="الإجمالي بالفترة المختارة" value={totalInvoicing} style={{ marginBottom: 16 }} />
          <Table columns={invColumns} dataSource={invRows} rowKey={(r, i) => i} loading={invLoading} pagination={{ pageSize: 20 }} size="small" />
        </Card>
      )
    },
    {
      key: 'contracts',
      label: <span><FileDoneOutlined /> عقود النقل الفعالة</span>,
      children: (
        <Card>
          <Statistic title="إجمالي العقود الفعالة" value={totalContracts} style={{ marginBottom: 16 }} valueStyle={{ color: '#0f8a45' }} />
          <Table columns={contractColumns} dataSource={contractRows} rowKey="customerName" loading={contractLoading} pagination={{ pageSize: 20 }} size="small" />
        </Card>
      )
    },
    {
      key: 'cars',
      label: <span><CarOutlined /> السيارات</span>,
      children: (
        <Row gutter={16}>
          <Col span={12}>
            <Card title={<span><CarOutlined /> سيارات جاهزة وغير مستخدَمة</span>} loading={carsLoading}>
              <Statistic value={availableCars.length} valueStyle={{ color: '#0f8a45' }} style={{ marginBottom: 16 }} />
              <Table columns={carColumns} dataSource={availableCars} rowKey="Guid" pagination={{ pageSize: 10 }} size="small" />
            </Card>
          </Col>
          <Col span={12}>
            <Card title={<span><ToolOutlined /> سيارات بالورشة</span>} loading={carsLoading}>
              <Statistic value={workshopCars.length} valueStyle={{ color: '#d4640a' }} style={{ marginBottom: 16 }} />
              <Table columns={carColumns} dataSource={workshopCars} rowKey="Guid" pagination={{ pageSize: 10 }} size="small" />
            </Card>
          </Col>
        </Row>
      )
    },
  ]

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>داشبورد النقليات</h2>
      <Tabs items={items} />
    </div>
  )
}

export default TransportDashboard
